import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowDown, Package, User } from 'lucide-react';
import { useAcademyStore } from '../../../store/academyStore';
import { ModuleSuccess } from '../../ui/ModuleSuccess';
import { LessonWrapper, LessonPage } from '../../ui/LessonWrapper';

// ============ LESSON CONTENT ============
const LESSONS: LessonPage[] = [
    {
        id: 1,
        title: "Passing Data",
        icon: "📦",
        content: (
            <div className="space-y-6">
                <div className="bg-blue-50 p-6 rounded-2xl border-2 border-blue-200">
                    <h3 className="font-bold text-xl text-blue-800 mb-3">Parent to Child</h3>
                    <p className="text-slate-700 mb-4">
                        Components need to talk to each other. We use <strong>Props</strong> (Properties) for this.
                    </p>
                    <p className="text-slate-700">
                        It's like passing a ball. The Parent throws it, and the Child catches it.
                    </p>
                </div>
            </div>
        )
    },
    {
        id: 2,
        title: "Arguments",
        icon: "🔧",
        content: (
            <div className="space-y-6">
                <div className="bg-white p-4 rounded-xl border border-slate-200">
                    <p className="text-slate-600 text-sm mb-2">
                        Props are just like <strong>arguments</strong> in a normal function.
                    </p>
                    <div className="font-mono text-sm bg-slate-100 p-2 rounded">
                        <span className="text-purple-600">function</span> Welcome(<span className="text-orange-600">name</span>) {'{'} ... {'}'}
                    </div>
                </div>
                <div className="bg-slate-900 text-blue-300 p-4 rounded-xl font-mono text-sm mt-4">
                    <div className="text-slate-500">// In JSX:</div>
                    <div>{'<Welcome name="Sam" />'}</div>
                </div>
            </div>
        )
    },
    {
        id: 3,
        title: "Prop Drilling",
        icon: "🕳️",
        content: (
            <div className="space-y-6">
                <div className="bg-red-50 p-6 rounded-2xl border-2 border-red-200">
                    <h3 className="font-bold text-xl text-red-800 mb-3">The Bucket Brigade</h3>
                    <p className="text-slate-700 mb-4">
                        Sometimes data needs to go <em>deep</em>. Parent &rarr; Child &rarr; Grandchild.
                    </p>
                    <p className="text-slate-700">
                        The middle components just pass it along. This is called <strong>Prop Drilling</strong>.
                    </p>
                </div>
                <div className="bg-blue-50 p-4 rounded-xl border border-blue-200">
                    <h4 className="font-bold text-blue-800 mb-2">🎮 Mission: Data Flow</h4>
                    <p className="text-blue-700 text-sm">
                        Change the props in the Parent controls to see the GrandChild update instantly.
                        Make 5 changes to pass!
                    </p>
                </div>
            </div>
        )
    }
];

// ============ GAME COMPONENT ============
interface DrillPropProps {
    color: string;
    item: string;
    depth: number;
}

// 3. GrandChild (The Bottom)
const GrandChild = ({ color, item, depth }: DrillPropProps) => (
    <div className={`p-4 rounded-xl border-2 ${color === 'red' ? 'border-red-500 bg-red-900/20' : color === 'blue' ? 'border-blue-500 bg-blue-900/20' : 'border-green-500 bg-green-900/20'} flex flex-col items-center gap-2 w-full transition-colors duration-500`}>
        <span className="text-xs font-bold uppercase tracking-widest opacity-50 text-slate-400">GrandChild</span>
        <motion.div
            key={`${color}-${item}`}
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className={`p-3 rounded-lg ${color === 'red' ? 'bg-red-500' : color === 'blue' ? 'bg-blue-500' : 'bg-green-500'} text-white font-bold flex items-center gap-2 shadow-lg w-full justify-center transition-colors duration-500`}
        >
            <Package size={16} /> {item}
        </motion.div>
        <div className="text-xs text-slate-400 mt-1 font-mono">Depth: {depth}</div>
    </div>
);

// 2. Child (The Middleman)
const Child = ({ color, item, depth }: DrillPropProps) => (
    <div className="p-6 rounded-2xl border-2 border-slate-700 bg-slate-800 flex flex-col items-center gap-4 w-full relative">
        <span className="text-xs font-bold uppercase tracking-widest text-slate-500">Child Component</span>

        {/* Visual drilling line */}
        <div className="absolute left-8 top-12 bottom-12 w-0.5 bg-dashed border-l-2 border-slate-700/50 border-dotted h-24"></div>

        <div className="flex flex-col items-center gap-2 w-full">
            <motion.div
                animate={{ y: [0, 5, 0] }}
                transition={{ repeat: Infinity, duration: 2 }}
            >
                <ArrowDown className="text-slate-500" />
            </motion.div>
            <GrandChild color={color} item={item} depth={depth + 1} />
        </div>
    </div>
);

// 1. Parent (The Source)
const Parent = ({ color, item }: { color: string; item: string }) => (
    <div className="p-8 rounded-3xl border-4 border-slate-600 bg-slate-900 w-full max-w-sm mx-auto flex flex-col items-center shadow-2xl">
        <span className="text-xs font-bold uppercase tracking-widest text-slate-400 mb-4">Parent Component</span>
        <Child color={color} item={item} depth={1} />
    </div>
);

const PropsGame = ({ onComplete }: { onComplete: () => void }) => {
    const [selectedColor, setSelectedColor] = useState('blue');
    const [selectedItem, setSelectedItem] = useState('Box');
    const [changes, setChanges] = useState(0);

    const checkWin = (count: number) => {
        if (count >= 5) {
            onComplete();
        }
    };

    const handleColorChange = (c: string) => {
        setSelectedColor(c);
        const newCount = changes + 1;
        setChanges(newCount);
        checkWin(newCount);
    };

    const handleItemChange = (i: string) => {
        setSelectedItem(i);
        const newCount = changes + 1;
        setChanges(newCount);
        checkWin(newCount);
    };

    return (
        <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
            {/* Visualizer */}
            <div className="flex flex-col items-center">
                <Parent color={selectedColor} item={selectedItem} />
            </div>

            {/* Controls */}
            <div className="space-y-8">
                <div className="bg-slate-900 p-8 rounded-3xl border border-slate-800">
                    <h2 className="text-2xl font-bold mb-6 flex items-center gap-3 text-white">
                        <User size={28} className="text-blue-400" /> Parent Controls
                    </h2>

                    <div className="space-y-6">
                        <div>
                            <label className="block text-sm font-bold text-slate-400 mb-2">Prop: Color</label>
                            <div className="flex gap-4">
                                {['red', 'blue', 'green'].map(c => (
                                    <button
                                        key={c}
                                        onClick={() => handleColorChange(c)}
                                        className={`w-12 h-12 rounded-full border-4 transition-transform hover:scale-110 ${selectedColor === c ? 'border-white scale-110 shadow-lg' : 'border-transparent opacity-50'} ${c === 'red' ? 'bg-red-500' : c === 'blue' ? 'bg-blue-500' : 'bg-green-500'}`}
                                    />
                                ))}
                            </div>
                        </div>

                        <div>
                            <label className="block text-sm font-bold text-slate-400 mb-2">Prop: ItemName</label>
                            <div className="flex gap-4">
                                {['Box', 'Banana', 'Sword'].map(i => (
                                    <button
                                        key={i}
                                        onClick={() => handleItemChange(i)}
                                        className={`px-4 py-2 rounded-lg border-2 font-bold transition-all ${selectedItem === i ? 'bg-slate-700 border-white text-white' : 'border-slate-700 text-slate-500 hover:border-slate-500'}`}
                                    >
                                        {i}
                                    </button>
                                ))}
                            </div>
                        </div>

                        <div className="pt-4 border-t border-slate-800">
                            <div className="text-xs text-slate-500 uppercase font-bold mb-2">Progress</div>
                            <div className="w-full bg-slate-800 h-2 rounded-full overflow-hidden">
                                <motion.div
                                    className="bg-blue-500 h-full"
                                    initial={{ width: 0 }}
                                    animate={{ width: `${Math.min(100, (changes / 5) * 100)}%` }}
                                />
                            </div>
                            <div className="text-right text-xs text-slate-400 mt-1">{Math.min(5, changes)}/5 Changes</div>
                        </div>
                    </div>
                </div>

                <div className="bg-blue-900/20 p-6 rounded-2xl border border-blue-500/30">
                    <h3 className="font-bold text-blue-300 mb-2">Prop Drilling</h3>
                    <p className="text-sm text-blue-200/80">
                        Notice how we change the data in the <strong>Parent</strong>, but the <strong>GrandChild</strong> updates? The data "drills" down through the Child.
                    </p>
                    <div className="mt-4 font-mono text-xs bg-slate-950 p-3 rounded text-slate-400">
                        {'<Child color={color} item={item} />'}
                    </div>
                </div>
            </div>
        </div>
    );
};

// ============ MAIN COMPONENT ============
export const ReactProps = () => {
    const { completeModule } = useAcademyStore();
    const [completed, setCompleted] = useState(false);

    const handleComplete = () => {
        setCompleted(true);
        completeModule('react-props', 100);
    };

    return (
        <LessonWrapper
            moduleName="React Props"
            moduleEmoji="📦"
            moduleColor="blue"
            lessons={LESSONS}
            completed={completed}
            activityLabel="Data Lab"
        >
            <PropsGame onComplete={handleComplete} />
        </LessonWrapper>
    );
};

export default ReactProps;
